export enum ImageryProviderParameterType {
    NUMBER = 'number',
    STRING = 'string',
    BOOLEAN = 'boolean',
    URL = 'url',
    ARRAY = 'array', // AKA, array of strings
    WMS_URL = 'wms_url',
    WMTS_URL = 'wmts_url',
    LAYERS = 'layers',
    IMS_DROPDOWN = 'ims_dropdown',
}

export interface LayerParameterDefinition {
    prettyParam: string;
    type: ImageryProviderParameterType;
    mapParam: string;
    defaultValue?: any;
    minimumLevel?: string;
    childProps?: any;
}

interface LayerTypeOptions {
    call: string;
    pretty: string;
    required: LayerParameterDefinition[];
    optional: LayerParameterDefinition[];
    isDefaultType?: boolean;
}

export type LayerTypeOptionsByTypeName = {
    [typeName: string]: LayerTypeOptions;
};

/**
 * This thing is necessary because the definitions are pulled from the API just after authorization.
 * This factory is used from that point on to initialise helper objects.
 */
export interface MapOptionsHelperFactory {
    createHelper(): MapLayerOptionsHelper;
}

export class LazyMapOptionsHelperFactory implements MapOptionsHelperFactory {
    private _mapOptionsDef: LayerTypeOptionsByTypeName;
    private _helper: MapLayerOptionsHelper;

    /**
     * Call this with the result of frontend_config/get_xxx_layer_definitions
     * @param {LayerTypeOptionsByTypeName} opts
     */
    setMapOptionsDef(opts: LayerTypeOptionsByTypeName): void {
        if (!opts) {
            throw new Error(`opts must be truthy`);
        }
        this._mapOptionsDef = opts;
        this._helper = new MapLayerOptionsHelper(this._mapOptionsDef);
    }

    createHelper(): MapLayerOptionsHelper {
        if (!this._helper) {
            throw new Error(`Factory not initialised`);
        }
        return this._helper;
    }
}

export class EmptyMapOptionsHelperFactory implements MapOptionsHelperFactory {
    createHelper(): MapLayerOptionsHelper {
        // we're not allowed to have no map option definitions, because that doesn't make sense... unless you're
        //  on the report page and there is no map!?
        // a valid factory that returns a nil instance... don't touch it jimmy or you'll die!
        return null;
    }
}

/**
 * Just something to help navigate the big map thing above.
 */
export class MapLayerOptionsHelper {
    private readonly _mapOptionsDef: LayerTypeOptionsByTypeName;

    constructor(mapOptionsDef: LayerTypeOptionsByTypeName) {
        if (!mapOptionsDef) {
            throw new Error(`mapOptionsDef must be truthy`);
        }
        this._mapOptionsDef = mapOptionsDef;
        if (Object.keys(this._mapOptionsDef).length === 0) {
            throw new Error(`Map options def must have > 0 entries`);
        }
    }

    getDefaultType(): string {
        const defaultType = Object.keys(this._mapOptionsDef).find(
            (key) => this._mapOptionsDef[key].isDefaultType,
        );
        if (defaultType) {
            return defaultType;
        }
        // Otherwise, just grab the first one
        return Object.keys(this._mapOptionsDef)[0];
    }

    getAvailableTypes(): LayerTypeOptionsByTypeName {
        return this._mapOptionsDef;
    }

    isValidType(type: string): boolean {
        return Object.keys(this._mapOptionsDef).includes(type);
    }

    getOptionalParamKeys(type: string): string[] {
        this._checkType(type);
        return this.getOptionalParams(type).map((t) => t.mapParam);
    }

    getOptionalParams(type: string): LayerParameterDefinition[] {
        this._checkType(type);
        return this._mapOptionsDef[type].optional || [];
    }

    getOptionalParamByKey(type: string, key: string): LayerParameterDefinition {
        this._checkType(type);
        const result = this.getOptionalParams(type).find((p) => p.mapParam === key);
        if (!result) {
            throw new Error(`No optional parameter for type: ${type} and key: ${key}`);
        }
        return result;
    }

    getRequiredParams(type: string): LayerParameterDefinition[] {
        this._checkType(type);
        return this._mapOptionsDef[type].required || [];
    }

    getRequiredParamKeys(type: string): string[] {
        this._checkType(type);
        return this.getRequiredParams(type).map((t) => t.mapParam);
    }

    private _checkType(type: string) {
        if (!this.isValidType(type)) {
            throw new Error(`type not handled ${type}`);
        }
    }
}
